// Copyright 2021 The Prometheus Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { EditorState } from '@codemirror/state';
import { SyntaxNode } from '@lezer/common';
import {
  EqlRegex,
  EqlSingle,
  LabelName,
  MatchOp,
  Neq,
  NeqRegex,
  QuotedLabelName,
  StringLiteral,
} from '../../lezer/parser.terms';
import { Matcher } from '../types';
import { labelNameHasUTF8Characters, metricHasUTF8Characters, quoteString } from './escaping';

function createMatcher(labelMatcher: SyntaxNode, state: EditorState): Matcher {
  const matcher = new Matcher(0, '', '');
  const cursor = labelMatcher.cursor();
  if (!cursor.next()) {
    // weird case, that would mean the labelMatcher doesn't have any child.
    return matcher;
  }
  do {
    switch (cursor.type.id) {
      case QuotedLabelName:
        matcher.name = state.sliceDoc(cursor.from, cursor.to).slice(1, -1);
        break;
      case LabelName:
        matcher.name = state.sliceDoc(cursor.from, cursor.to);
        break;
      case MatchOp:
        const ope = cursor.node.firstChild;
        if (ope) {
          matcher.type = ope.type.id;
        }
        break;
      case StringLiteral:
        matcher.value = state.sliceDoc(cursor.from, cursor.to).slice(1, -1);
        break;
    }
  } while (cursor.nextSibling());
  return matcher;
}

export function buildLabelMatchers(labelMatchers: SyntaxNode[], state: EditorState): Matcher[] {
  const matchers: Matcher[] = [];
  labelMatchers.forEach((value) => {
    matchers.push(createMatcher(value, state));
  });
  return matchers;
}

export function labelMatchersToString(metricName: string, matchers?: Matcher[], labelName?: string): string {
  // When matchers include a positive __name__ filter (= or =~) that won't be excluded
  // by labelName, prefer it over metricName to preserve the match operator (e.g., =~).
  // Negative matchers (!= or !~) are supplementary filters and should not replace metricName.
  const isPositiveNameMatcher = (m: Matcher) =>
    m.name === '__name__' && m.value !== '' && (m.type === EqlSingle || m.type === EqlRegex);

  const hasMatchers = matchers && matchers.length > 0;
  const hasActiveNameMatcher = labelName !== '__name__' && (matchers?.some(isPositiveNameMatcher) ?? false);
  const effectiveMetricName = hasActiveNameMatcher ? '' : metricName;

  const needsQuoting = metricHasUTF8Characters(effectiveMetricName);
  const quotedMetricName = needsQuoting ? quoteString(effectiveMetricName) : '';

  if (!hasMatchers) {
    if (needsQuoting) {
      return `{${quotedMetricName}}`;
    }
    return effectiveMetricName;
  }
  // Build the matchers string
  const matcherStrings: string[] = [];
  for (const matcher of matchers) {
    if (matcher.name === labelName || matcher.value === '') {
      continue;
    }
    let type = '';
    switch (matcher.type) {
      case EqlSingle:
        type = '=';
        break;
      case Neq:
        type = '!=';
        break;
      case NeqRegex:
        type = '!~';
        break;
      case EqlRegex:
        type = '=~';
        break;
      default:
        type = '=';
    }
    // At this point we don't know how original string was escaped, so we just add double quotes.
    // This breaks if the original matcher name or value were surrounded with single quotes or backticks.
    //
    const matcherName = labelNameHasUTF8Characters(matcher.name) ? `"${matcher.name}"` : matcher.name;
    const m = `${matcherName}${type}"${matcher.value}"`;
    matcherStrings.push(m);
  }

  const matchersAsString = matcherStrings.join(',');

  if (matcherStrings.length === 0) {
    if (needsQuoting) {
      return `{${quotedMetricName}}`;
    }
    return effectiveMetricName;
  }

  // If metric name needs quoting, use the quoted label matcher format: {"metricName",...}
  if (needsQuoting) {
    return `{${quotedMetricName},${matchersAsString}}`;
  }

  return `${effectiveMetricName}{${matchersAsString}}`;
}
